import styled from 'styled-components';
import type { HighlightState } from './FieldBindingBox.types.ts';

// Blue highlight scheme (matches the design):
//   selected → thick dark-blue outline + soft blue fill
//   bound    → soft blue fill + a lighter blue border
//   unbound  → soft grey fill + a dashed grey outline ("still needs a source")
// Hovering lifts the inner fill to a slightly stronger blue in every state.
//
// Both fills are OPAQUE on purpose: a template whose PDF already carries values prints them
// inside these very rects, and a see-through box let the label and the page's own text render
// on top of each other.
const borderColorFor = (
	state: HighlightState,
	selected: boolean,
	colors: { blue: string[]; gray: string[] }
) => {
	if (selected) {
		return colors.blue[6];
	}
	return state === 'bound' ? colors.blue[3] : colors.gray[6];
};

const backgroundColorFor = (state: HighlightState, colors: { blue: string[]; gray: string[] }) =>
	state === 'bound' ? colors.blue[0] : colors.gray[2];

export const Shell = styled.div<{ $state: HighlightState; $selected: boolean }>`
	position: relative;
	width: 100%;
	height: 100%;
	box-sizing: border-box;
	cursor: move;
	user-select: none;
	border-radius: 4px;
	border-width: ${({ $selected }) => ($selected ? '2px' : '1px')};
	border-style: ${({ $state }) => ($state === 'bound' ? 'solid' : 'dashed')};
	border-color: ${({ $state, $selected, theme }) =>
		borderColorFor($state, $selected, theme.v3.colors)};
	background-color: ${({ $state, theme }) => backgroundColorFor($state, theme.v3.colors)};
	display: flex;
	align-items: center;
	padding: 0 4px;
	overflow: visible;
	transition:
		background-color 0.12s ease,
		border-color 0.12s ease;

	&:hover {
		background-color: ${({ theme }) => theme.v3.colors.blue[1]};
		border-color: ${({ $selected, theme }) =>
			$selected ? theme.v3.colors.blue[7] : theme.v3.colors.blue[4]};
	}
`;

export const Label = styled.span`
	/* min-width: 0 or the flex child refuses to shrink below its text width, the ellipsis never
	   fires, and a long name spills past the Shell (overflow: visible, for the × button) onto
	   the document. */
	flex: 1;
	min-width: 0;
	font-size: 11px;
	line-height: 1.2;
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
	color: ${({ theme }) => theme.v3.colors.blue[8]};
`;

export const RemoveButton = styled.button`
	position: absolute;
	top: -8px;
	right: -8px;
	width: 16px;
	height: 16px;
	padding: 0;
	display: flex;
	align-items: center;
	justify-content: center;
	border-radius: 50%;
	border: none;
	cursor: pointer;
	background-color: ${({ theme }) => theme.v3.colors.red[7]};
	color: #fff;
	/* Above react-rnd's resize handles so the button, not the handle, gets the
	   pointer at the top-right corner. */
	z-index: 10;
`;
